// Docs: IMPLEMENTATION.md §3.1. Update when changing the React root/bootstrap.
import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import './index.css';
import App from './App';
import { initAnalytics, identifyFromToken } from './analytics';

// Recover from stale-chunk loads after a redeploy: a tab left open across a
// deploy requests an old hashed chunk, the SPA fallback serves index.html as
// JS ("'text/html' is not a valid JavaScript MIME type") and the route dies.
// Reload once to pick up the new bundle; the timestamp guard stops a reload
// loop if the fresh bundle fails too (e.g. offline).
window.addEventListener('vite:preloadError', (event) => {
  let lastReloadAt = 0;
  try {
    lastReloadAt = Number(sessionStorage.getItem('chunkReloadAt')) || 0;
  } catch { /* storage unavailable — still reload, just without the guard */ }
  if (Date.now() - lastReloadAt < 60000) return;
  try {
    sessionStorage.setItem('chunkReloadAt', String(Date.now()));
  } catch { /* ignore */ }
  event.preventDefault();
  window.location.reload();
});

// Initialize Sentry error tracking (replay deferred for faster startup)
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_VERSION || '1.0.0',
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
    // Performance monitoring
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
    // Session replay for errors (integration added lazily below)
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    // Filter sensitive data
    beforeSend(event) {
      // Remove any potential PII from error reports
      if (event.request?.headers) {
        delete event.request.headers['Authorization'];
        delete event.request.headers['Cookie'];
      }
      return event;
    },
  });

  // Defer replay integration until after first paint
  setTimeout(() => {
    Sentry.addIntegration(Sentry.replayIntegration({
      maskAllText: true,
      blockAllMedia: true,
    }));
  }, 3000);
}

initAnalytics();
identifyFromToken(localStorage.getItem('jwt'));

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={<div>An error has occurred</div>}>
      <App />
    </Sentry.ErrorBoundary>
  </React.StrictMode>
); 